import * as React from 'react';
import {useJsonForms, withJsonFormsControlProps} from '@jsonforms/react';
import Radio from './Radio';
import {SchemaUtils} from "../../util/SchemaUtils";
import {ControlValueFactory} from "../../factory/ControlValueFactory";
import {Component} from "../../enum/Component";
import { useMemo, useCallback } from 'react';

interface RadioControlProps {
    data: any;
    label: any;
    description?: any;
    handleChange(path: string, value: string): void;
    path: string;
    uischema: any;
    schema: any;
    errors: any;
    rootSchema: any;
    appValidated?: boolean;
}

const RadioControl = ({ errors, uischema, schema, handleChange, path, data, rootSchema, appValidated }: RadioControlProps) => {

    const jsonForms = useJsonForms();

    // Memoize expensive computations
    const enabled = useMemo(() =>
            SchemaUtils.isComponentEnabled(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const value = useMemo(() =>
            ControlValueFactory.getControlValue(Component.Radio, data, uischema.scope, jsonForms, enabled && visible),
        [Component.Radio, data, uischema.scope, jsonForms, enabled, visible]
    );

    // Remove local state - use the value directly to avoid sync issues
    const handleRadioChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(path, event.target.value);
    }, [handleChange, path]);

    // Memoize the Radio component props to prevent unnecessary re-renders
    const radioProps = useMemo(() => ({
        error: errors,
        value: value || '', // Use value directly, provide fallback
        required: required,
        enums: schema.enum || [],
        label: uischema.label,
        description: schema.description,
        scope: uischema.scope,
        onClick: handleRadioChange,
        enabled: enabled,
        visible: visible,
        appValidated: appValidated
    }), [
        errors,
        value,
        required,
        schema.enum,
        uischema.label,
        schema.description,
        uischema.scope,
        handleRadioChange,
        enabled,
        visible,
        appValidated
    ]);

    return <Radio {...radioProps} />;
};

export default React.memo(withJsonFormsControlProps(RadioControl));
